import { legacyLocalLabel, legacyLocalDate } from '../../../../utilities/legacyDatePresentation';
import { subMonths, addMonths, startOfWeek, endOfWeek, addDays, startOfDay, startOfMonth, endOfMonth } from 'date-fns';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarFold, ChevronLeft, ChevronRight } from 'lucide-react';
import HeaderCalendarEvents from './HeaderCalendarEvents';
import { useAuth } from '../../../../firebase/AuthProvider';
import { AllEvent } from '../../../../interfaces/identity/calendar/Events';
import { getAllEvents } from '../../../../services/calendarService/EventService';
import ModalMeet from '../../../components/calendar/ModalMeet';
import ViewEvent from '../../../components/calendar/events/ViewEvent';

export default function HeaderCalendarDropdown() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const token = (currentUser as any)?.accessToken;

    const [isOpen, setIsOpen] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [events, setEvents] = useState<AllEvent[]>([]);
    
    // Modal States
    const [selectedEvent, setSelectedEvent] = useState<AllEvent | null>(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchEvents = useCallback(async () => {
        if (!token) return;
        try {
            const start = new Date(startOfWeek(startOfMonth(new Date(currentMonth))));
            const end = new Date(endOfWeek(endOfMonth(new Date(currentMonth))));
            const calendarFeed = await getAllEvents(token, start, end);
            setEvents(calendarFeed?.events ?? []);
        } catch (error) {
            console.error("Failed to fetch events", error);
        }
    }, [currentMonth, token]);

    useEffect(() => {
        if (isOpen) {
            fetchEvents();
        }
    }, [isOpen, fetchEvents]);

    const handleEventClick = (event: AllEvent) => {
        setSelectedEvent(event);
        setIsViewModalOpen(true);
        setIsOpen(false); // Close the dropdown
    };

    const gridDays = (() => {
        const start = startOfWeek(startOfMonth(new Date(currentMonth)));
        const end = endOfWeek(endOfMonth(new Date(currentMonth)));
        const days = [];
        let day = new Date(start);
        while ((+day <= +end)) {
            days.push(new Date(day));
            day = addDays(day, 1);
        }
        return days;
    })();

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                aria-label="Open calendar"
                aria-expanded={isOpen}
                className={`app-header-icon-button relative flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-600 transition-colors hover:bg-gray-50 hover:text-[#7E57C2] dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-purple-300 ${isOpen ? "bg-purple-50 text-[#7E57C2] dark:bg-purple-400/10 dark:text-purple-300" : ""}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <CalendarFold size={18} strokeWidth={2} />
            </button>

            <div className={`dark-popup absolute right-0 top-[130%] flex bg-white dark:bg-slate-900 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-gray-100 z-[100] transition-all duration-300 origin-top-right overflow-hidden ${
                isOpen ? 'scale-100 opacity-100 visible' : 'scale-95 opacity-0 invisible pointer-events-none'
            }`}>
                
                <HeaderCalendarEvents 
                    selectedDate={selectedDate} 
                    events={events} 
                    isOpen={!!selectedDate} 
                    onEventClick={handleEventClick}
                />

                {/* Applied the p-2 adjustment here */}
                <div className="dark-surface w-[300px] p-2 shrink-0 bg-white dark:bg-slate-900 z-10 h-[340px] flex flex-col">
                    <div className="flex items-center justify-between mb-4 px-2 pt-2">
                        <button onClick={() => setCurrentMonth((prev: any) => subMonths(prev, 1))} className="p-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg transition-colors text-gray-400 dark:text-slate-500 hover:text-[#7E57C2] dark:hover:text-purple-300">
                            <ChevronLeft size={16} strokeWidth={2.5} />
                        </button>
                        <h3 
                            onClick={() => { setIsOpen(false); navigate('/calendar'); }}
                            className="text-[14px] font-semibold text-gray-800 dark:text-slate-100 m-0 cursor-pointer hover:text-[#7E57C2] dark:hover:text-purple-300 transition-colors uppercase tracking-wide"
                            title="Go to full calendar"
                        >
                            {legacyLocalLabel(currentMonth, "MMMM yyyy")}
                        </h3>
                        <button onClick={() => setCurrentMonth((prev: any) => addMonths(prev, 1))} className="p-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg transition-colors text-gray-400 dark:text-slate-500 hover:text-[#7E57C2] dark:hover:text-purple-300">
                            <ChevronRight size={16} strokeWidth={2.5} />
                        </button>
                    </div>

                    <div className="grid grid-cols-7 mb-3">
                        {weekDays.map(day => (
                            <div key={day} className="text-[10px] font-semibold text-gray-400 text-center uppercase tracking-wider">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-y-2 flex-1 pb-2">
                        {gridDays.map(day => {
                            const isCurrentMonth = (day).getMonth() === (currentMonth).getMonth();
                            const isToday = (+startOfDay(day) === +startOfDay(new Date()));
                            const isSelected = selectedDate && (+startOfDay(day) === +startOfDay(selectedDate));
                            const hasEvents = events.some(e => (+startOfDay(legacyLocalDate(e.startTime)) === +startOfDay(day)));

                            return (
                                <div key={legacyLocalLabel(day, "yyyy-MM-dd")} className="flex justify-center items-center">
                                    <button
                                        onClick={() => setSelectedDate(isSelected ? null : new Date(day))}
                                        onDoubleClick={() => { setIsOpen(false); navigate('/calendar'); }}
                                        className={`relative w-8 h-8 flex items-center justify-center rounded-full text-[13px] font-semibold transition-all select-none
                                            ${!isCurrentMonth ? 'text-gray-300 dark:text-slate-600' : 'text-gray-700 dark:text-slate-300'}
                                            ${isToday ? 'bg-[#7E57C2] text-white shadow-md' : 'hover:bg-purple-50 dark:hover:bg-purple-400/10 hover:text-[#7E57C2] dark:hover:text-purple-300'}
                                            ${isSelected && !isToday ? 'ring-2 ring-[#7E57C2] dark:ring-purple-300/70 ring-offset-1 dark:ring-offset-zinc-900 text-[#7E57C2] dark:text-purple-200 bg-purple-50 dark:bg-purple-400/20' : ''}
                                        `}
                                    >
                                        <span className="mb-[1px]">{(day).getDate()}</span>
                                        {hasEvents && (
                                            <span className={`absolute bottom-[4px] w-1 h-1 rounded-full ${isToday ? 'bg-white' : 'bg-[#7E57C2]'}`} />
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* View Event Modal rendered completely outside the dropdown layout flow */}
            <ModalMeet isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)}>
                <ViewEvent 
                    event={selectedEvent} 
                    onClose={() => setIsViewModalOpen(false)} 
                    refreshAllEvents={fetchEvents} 
                />
            </ModalMeet>
        </div>
    );
}
